import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Crown, Target, Users, Megaphone, Star, TrendingUp,
  ChevronRight, Award, Zap, Heart, Brain, MessageCircle,
  Lightbulb, PenTool, CheckCircle, Shield, Trophy, BookOpen
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import Navigation from '@/components/Navigation';
import ProfileCompletionModal from '@/components/ProfileCompletionModal';
import NotificationBell from '@/components/NotificationBell';
import GoalReminders from '@/components/GoalReminders';
import MandatoryCheckinModal from '@/components/MandatoryCheckinModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const YouthLeadershipDashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const [loading, setLoading] = useState(true);
  const [dashData, setDashData] = useState(null);
  const [aiGuidance, setAiGuidance] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchDashboard(); fetchGuidance(); }, []);

  const fetchGuidance = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ai/guidance?section=home`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      if (res.ok) setAiGuidance(await res.json());
    } catch (e) { console.error('Dashboard error:', e); }
  };

  const fetchDashboard = async () => {
    try {
      const res = await fetch(`${API_URL}/api/gps/dashboard`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) setDashData(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleLogout = () => { localStorage.removeItem('token'); setUser(null); };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5F3EF' }}>
        <Crown className="w-12 h-12 animate-pulse" style={{ color: colors.primary }} />
      </div>
    );
  }

  const leadershipPillars = [
    { id: 'vision', label: 'Personal Vision', icon: Lightbulb, done: (dashData?.interests_count || 0) > 0, count: dashData?.interests_count || 0, route: '/interests', color: '#F59E0B' },
    { id: 'goals', label: 'Leadership Goals', icon: Target, done: (dashData?.active_goals || 0) > 0, count: dashData?.active_goals || 0, route: '/goals', color: '#10B981' },
    { id: 'skills', label: 'Leadership Skills', icon: Crown, done: (dashData?.skills_count || 0) > 0, count: dashData?.skills_count || 0, route: '/skills', color: '#8B5CF6' },
    { id: 'community', label: 'Community Impact', icon: Users, done: false, count: 0, route: '/wins', color: '#EC4899' },
    { id: 'portfolio', label: 'Leadership Portfolio', icon: Award, done: (dashData?.vault_items_count || 0) > 0, count: dashData?.vault_items_count || 0, route: '/vault', color: '#3B82F6' },
  ];

  const completedCount = leadershipPillars.filter(a => a.done).length;
  const progressPercent = Math.round((completedCount / leadershipPillars.length) * 100);

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: '#F5F3EF' }}>
      <ProfileCompletionModal user={user} onComplete={(u) => setUser(u)} />
      <MandatoryCheckinModal user={user} onComplete={() => fetchDashboard()} />
      <GoalReminders user={user} />
      <div className="relative overflow-hidden" style={{
        background: `linear-gradient(135deg, ${colors.primary || '#E86C3D'} 0%, ${colors.primary_dark || '#C4542D'} 100%)`
      }}>
        <div className="relative p-6 pb-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl" style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}>
                  {user?.avatar_emoji || '👑'}
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white" data-testid="leadership-welcome">
                    Hey, {user?.name?.split(' ')[0] || 'Leader'}
                  </h1>
                  <p className="text-white/70 text-sm">{user?.program_name || 'Youth Leadership'}</p>
                </div>
              </div>
              <div className="flex items-center gap-2"><NotificationBell /><Button variant="ghost" size="sm" onClick={handleLogout} className="text-white/80 hover:text-white hover:bg-white/10">Log out</Button></div>
            </div>

            <div className="mt-4 p-4 rounded-2xl" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-white/80 text-sm font-medium">Leadership Journey</span>
                <span className="text-white font-bold">{progressPercent}%</span>
              </div>
              <Progress value={progressPercent} className="h-3 bg-white/20" />
              <p className="text-gray-600 text-xs mt-2">{completedCount}/{leadershipPillars.length} pillars developed</p>
            </div>

            <div className="grid grid-cols-3 gap-3 mt-4">
              <div className="text-center p-3 rounded-xl" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
                <p className="text-2xl font-bold text-white">{dashData?.total_points || 0}</p>
                <p className="text-gray-600 text-xs">Points</p>
              </div>
              <div className="text-center p-3 rounded-xl" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
                <p className="text-2xl font-bold text-white">{dashData?.active_goals || 0}</p>
                <p className="text-gray-600 text-xs">Active Goals</p>
              </div>
              <div className="text-center p-3 rounded-xl" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
                <p className="text-2xl font-bold text-white">{dashData?.skills_count || 0}</p>
                <p className="text-gray-600 text-xs">Skills</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 -mt-4 space-y-5">
        {aiGuidance?.message && (
          <Card className="p-4 rounded-2xl" style={{ background: 'linear-gradient(135deg, #EDE9FE 0%, #FEF3C7 100%)', border: '1px solid #DDD6FE' }}>
            <div className="flex gap-3">
              <div className="w-9 h-9 rounded-xl bg-orange-500 flex items-center justify-center flex-shrink-0"><Brain className="w-5 h-5 text-white" /></div>
              <div><p className="text-xs font-bold text-orange-800 mb-0.5">Your AI Guide</p><p className="text-sm text-gray-700">{aiGuidance.message}</p>
                {aiGuidance.celebration && <div className="mt-2 p-2 rounded-lg bg-green-50 border border-green-200"><p className="text-xs text-green-700 font-medium">{aiGuidance.celebration}</p></div>}
                {aiGuidance.suggestions?.map((s, i) => <button key={i} onClick={() => navigate(s.action)} className="flex items-center gap-2 w-full p-2 mt-1 rounded-lg bg-white/80 hover:bg-white text-left"><span className="text-xs text-gray-700">{s.text}</span></button>)}
              </div>
            </div>
          </Card>
        )}

        <Card className="p-5 rounded-2xl shadow-lg" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: colors.text }}>
            <Crown className="w-5 h-5" style={{ color: colors.primary }} />
            Leadership Pillars
          </h2>
          <div className="space-y-3">
            {leadershipPillars.map((pillar, idx) => (
              <motion.div key={pillar.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: idx * 0.1 }}
                onClick={() => navigate(pillar.route)}
                className="flex items-center gap-4 p-4 rounded-xl cursor-pointer transition-all hover:shadow-md"
                style={{ backgroundColor: pillar.done ? `${pillar.color}08` : colors.surface, border: `1px solid ${pillar.done ? `${pillar.color}30` : colors.border}` }}
                data-testid={`pillar-${pillar.id}`}
              >
                <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: pillar.done ? `${pillar.color}15` : '#f3f4f6' }}>
                  <pillar.icon className="w-5 h-5" style={{ color: pillar.done ? pillar.color : '#9CA3AF' }} />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-sm" style={{ color: colors.text }}>{pillar.label}</p>
                  <p className="text-xs" style={{ color: colors.text_muted }}>{pillar.done ? `${pillar.count} added` : 'Get started'}</p>
                </div>
                {pillar.done ? (
                  <Badge className="text-xs" style={{ backgroundColor: `${pillar.color}15`, color: pillar.color }}>Done</Badge>
                ) : (
                  <ChevronRight className="w-5 h-5" style={{ color: colors.text_muted }} />
                )}
              </motion.div>
            ))}
          </div>
        </Card>

        <div className="grid grid-cols-2 gap-3">
          <Card className="p-4 rounded-2xl cursor-pointer hover:shadow-md" style={{ backgroundColor: colors.card, borderColor: colors.border }} onClick={() => navigate('/ai-guide')}>
            <Brain className="w-8 h-8 mb-2" style={{ color: '#8B5CF6' }} />
            <p className="font-bold text-sm" style={{ color: colors.text }}>AI Mentor</p>
            <p className="text-xs" style={{ color: colors.text_muted }}>Leadership coaching</p>
          </Card>
          <Card className="p-4 rounded-2xl cursor-pointer hover:shadow-md" style={{ backgroundColor: colors.card, borderColor: colors.border }} onClick={() => navigate('/helpdesk')}>
            <MessageCircle className="w-8 h-8 mb-2" style={{ color: colors.primary }} />
            <p className="font-bold text-sm" style={{ color: colors.text }}>Ask Coach</p>
            <p className="text-xs" style={{ color: colors.text_muted }}>Get guidance</p>
          </Card>
        </div>

        {/* Quick Links */}
        <div className="flex gap-2 overflow-x-auto pb-1" style={{ WebkitOverflowScrolling: 'touch' }}>
          {[
            { icon: Trophy, label: 'Rankings', path: '/leaderboard' },
                  { icon: Users, label: 'People', path: '/people-like-you' },
            { icon: Award, label: 'Wins', path: '/wins' },
            { icon: Star, label: 'Assess', path: '/custom-assessments' },
            { icon: BookOpen, label: 'Courses', path: '/workshops' },
            { icon: Target, label: 'Goals', path: '/goals' },
          ].map(item => (
            <button key={item.label} onClick={() => navigate(item.path)} className="flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-xl shadow-sm hover:shadow-md transition-all" style={{ backgroundColor: colors.card }}>
              <item.icon className="w-4 h-4" style={{ color: colors.primary }} />
              <span className="text-xs font-bold" style={{ color: colors.text }}>{item.label}</span>
            </button>
          ))}
        </div>
      </div>

      <Navigation role="youth" />
    </div>
  );
};

export default YouthLeadershipDashboard;
